import { Carousel } from "antd";
import React, { ReactElement, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import styles from "./style.module.scss";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";

export interface VLCarouselProps {
  contentList: ReactElement[];
  afterChange?: (index: number) => void;
  totalText?: string;
  selectedItem?: number;
}

const MINIMUM_SLIDES_TO_SHOW = 6;

export default function VLCarousel({
  contentList,
  afterChange,
  totalText,
  selectedItem = 0,
}: VLCarouselProps) {
  const carouselRef = useRef<any>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [currentSlide, setCurrentSlide] = useState(selectedItem);

  const slidesToShow =
    contentList.length > MINIMUM_SLIDES_TO_SHOW
      ? MINIMUM_SLIDES_TO_SHOW
      : contentList.length;

  useEffect(() => {
    wrapperRef.current?.focus();
    if (carouselRef.current && selectedItem !== currentSlide) {
      carouselRef.current.goTo(selectedItem, false);
      setCurrentSlide(selectedItem);
    }
  }, [selectedItem, currentSlide]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.buttons}>
        <div className={styles.totalText}>
          {totalText
            ? `${totalText} ${contentList.length}`
            : `Additional Items: ${contentList.length}`}
        </div>
        <div className={styles.centerButtons}>
          <div
            className={styles.button}
            onClick={() => carouselRef.current?.prev()}
          >
            <ArrowLeftOutlined />
          </div>
          <div>{`${currentSlide + 1} / ${contentList.length}`}</div>
          <div
            className={styles.button}
            onClick={() => carouselRef.current?.next()}
          >
            <ArrowRightOutlined />
          </div>
        </div>
      </div>

      <div
        ref={wrapperRef}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "ArrowLeft") carouselRef.current?.prev();
          if (e.key === "ArrowRight") carouselRef.current?.next();
        }}
      >
        <Carousel
          ref={carouselRef}
          initialSlide={selectedItem}
          dots={false}
          arrows={false}
          slidesToShow={slidesToShow}
          lazyLoad={"ondemand"}
          slidesToScroll={1}
          centerMode={true}
          draggable={true}
          afterChange={(index) => {
            setCurrentSlide(index);
            afterChange?.(index);
          }}
        >
          {contentList.map((content, index) => (
            <div
              key={index}
              className={classNames(styles.content)}
              onClick={() => carouselRef.current?.goTo(index)}
            >
              {content}
            </div>
          ))}
        </Carousel>
      </div>
    </div>
  );
}
